import React, { useState, useMemo, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { marked } from 'marked';
import TurndownService from 'turndown';

/**
 * RichTextEditor - A reusable WYSIWYG editor
 *
 * Props:
 *   value: string - The content to edit (markdown or HTML depending on mode)
 *   onChange: function(content) - Called when content changes
 *   placeholder: string - Placeholder text
 *   height: string - Editor height (default: '300px')
 *   toolbar: 'full' | 'basic' | 'minimal' - Toolbar configuration
 *   className: string - Additional CSS classes
 *   mode: 'markdown' | 'html' - Storage format (default: 'markdown' for backwards compatibility)
 *
 * Usage (HTML mode - recommended for new code):
 *   <RichTextEditor
 *     key={contentId}
 *     value={htmlContent}
 *     onChange={(html) => setHtmlContent(html)}
 *     mode="html"
 *   />
 *
 * Usage (Markdown mode - legacy):
 *   <RichTextEditor
 *     key={contentId}
 *     value={markdown}
 *     onChange={(md) => setMarkdown(md)}
 *     mode="markdown"
 *   />
 */
const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Start writing...',
  height = '300px',
  toolbar = 'full',
  className = '',
  mode = 'markdown'  // 'markdown' or 'html'
}) => {
  // Initialize HTML content based on mode
  const [editorHtml, setEditorHtml] = useState(() => {
    if (!value) return '';

    if (mode === 'html') {
      // HTML mode: use value directly
      return value;
    } else {
      // Markdown mode: convert to HTML for display
      try {
        return marked(value, { breaks: true, gfm: true });
      } catch (e) {
        console.error('Error converting markdown to HTML:', e);
        return value;
      }
    }
  });

  // Update editor when value prop changes (for controlled component behavior)
  useEffect(() => {
    if (mode === 'html') {
      setEditorHtml(value || '');
    }
  }, [value, mode]);

  // Configure turndown for markdown conversion (only used in markdown mode)
  const turndownService = useMemo(() => {
    if (mode === 'html') return null;

    const service = new TurndownService({
      headingStyle: 'atx',
      codeBlockStyle: 'fenced',
      bulletListMarker: '-'
    });

    // Keep line breaks
    service.addRule('lineBreaks', {
      filter: 'br',
      replacement: () => '\n'
    });

    return service;
  }, [mode]);

  // Handle editor changes
  const handleChange = (html) => {
    setEditorHtml(html);

    if (onChange) {
      if (mode === 'html') {
        // HTML mode: return HTML directly (with cleanup)
        const cleanHtml = html
          .replace(/<p><br><\/p>/g, '')  // Remove empty paragraphs
          .replace(/^\s+|\s+$/g, '');     // Trim whitespace
        onChange(cleanHtml);
      } else {
        // Markdown mode: convert HTML back to markdown
        try {
          const cleanHtml = html
            .replace(/<p><br><\/p>/g, '\n')
            .replace(/<p><\/p>/g, '\n');

          const markdown = turndownService.turndown(cleanHtml);
          onChange(markdown);
        } catch (e) {
          console.error('Error converting HTML to markdown:', e);
          onChange(html);
        }
      }
    }
  };

  // Toolbar configurations
  const toolbarOptions = {
    full: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean']
    ],
    basic: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
    minimal: [
      ['bold', 'italic'],
      [{ 'list': 'bullet' }],
      ['clean']
    ]
  };

  const modules = useMemo(() => ({
    toolbar: toolbarOptions[toolbar] || toolbarOptions.full
  }), [toolbar]);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'blockquote', 'code-block',
    'link'
  ];

  return (
    <div className={`rich-text-editor ${className}`}>
      <ReactQuill
        theme="snow"
        value={editorHtml}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={{ height, marginBottom: '50px' }}
      />
      <style>{`
        .rich-text-editor .ql-container {
          font-size: 14px;
          font-family: inherit;
        }
        .rich-text-editor .ql-editor {
          min-height: ${height};
        }
        .rich-text-editor .ql-editor p {
          margin-bottom: 0.5em;
        }
        .rich-text-editor .ql-toolbar {
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;
          background: #f8f9fa;
        }
        .rich-text-editor .ql-container {
          border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
